import React, { useEffect, useState } from "react";
import {
  Button,
  FormControl,
  TextField,
  Stack,
  InputAdornment,
  Autocomplete,
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  Fade,
  Slide,
  Divider,
  useTheme,
} from "@mui/material";
import { useQuery } from "@apollo/client";
import AddIngredients from "../inventory/AddIngredients";
import { useNotification } from "../../Context/notificationContext";
import { GET_INGREDIENTS } from "./ChooseGrainsForm";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

export default function PourBowlForm({ task, onSubmit }) {
  const [bowlName, setBowlName] = useState(task.item_name || "");
  const [quantity, setQuantity] = useState(task.quantity || "");
  const [itemError, setItemError] = useState("");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { showNotification } = useNotification();
  const minQuantity = 1;
  const maxQuantity = 1000;
  const theme = useTheme();

  const {
    data,
    loading: getIngredientsLoading,
    error: getIngredientsError,
    refetch: refetchIngredients,
  } = useQuery(GET_INGREDIENTS, {
    variables: {
      ingredientType: "ALL",
    },
    fetchPolicy: "cache-and-network",
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    
    try {
      if (validateForm()) {
        task.args = `${bowlName} - ${quantity}g`;
        task.quantity = quantity;
        task.item_name = bowlName;
        await onSubmit(task);
        showNotification("success", "Bowl successfully configured");
      }
    } catch (error) {
      showNotification("error", `Failed to submit: ${error.message}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  const validateForm = () => {
    if (!bowlName || bowlName.trim() === "") {
      setItemError("Please select or enter a bowl item name");
      setError("");
      return false;
    }
    
    if (!quantity || quantity === "") {
      setError("Please enter a quantity");
      setItemError("");
      return false;
    }
    
    if (isNaN(quantity) || +quantity < minQuantity || +quantity > maxQuantity) {
      setError(
        `Quantity must be a number between ${minQuantity} and ${maxQuantity} grams`
      );
      setItemError("");
      return false;
    }
    
    setError("");
    setItemError("");
    return true;
  };

  const [addIngredientActive, setAddIngredientActive] = useState(false);
  const [item_name, setItem_name] = useState("");
  const [ingredientType, setIngredientType] = useState(null);
  const [ingredientImage, setIngredientImage] = useState(null);
  const [ingredients, setIngredients] = useState([]);

  useEffect(() => {
    if (data && data.getIngredients) {
      setIngredients(data.getIngredients);
    }

    if (getIngredientsError) {
      showNotification("error", "Failed to load ingredients. Please try again.");
    }
  }, [data, getIngredientsError, showNotification]);

  if (addIngredientActive) {
    return (
      <Fade in={addIngredientActive}>
        <Box>
          <AddIngredients
            newIngredientName={item_name}
            setNewIngredientName={setItem_name}
            ingredientType={ingredientType}
            setIngredientType={setIngredientType}
            ingredientImage={ingredientImage}
            setIngredientImage={setIngredientImage}
            ingredients={ingredients}
            setIngredients={setIngredients}
            setAddIngredientActive={setAddIngredientActive}
            refetchIngredients={refetchIngredients}
          />
        </Box>
      </Fade>
    );
  }

  return (
    <Slide direction="up" in={!addIngredientActive} mountOnEnter unmountOnExit>
      <Paper elevation={3} sx={{ p: 3, borderRadius: 2, backgroundColor: theme.palette.primary.paper }}>
        <Typography variant="h5" component="h2" gutterBottom color="primary" sx={{ mb: 3, fontWeight: 600 }}>
          Configure Bowl
        </Typography>
        
        {getIngredientsError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Failed to load ingredients. Please try refreshing.
          </Alert>
        )}
        
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <Autocomplete
              options={data?.getIngredients || []}
              getOptionLabel={(option) => option.ingredient_name || ""}
              filterOptions={(options, { inputValue }) =>
                options.filter((option) =>
                  option.ingredient_name
                    .toLowerCase()
                    .includes(inputValue.toLowerCase())
                )
              }
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Bowl Item Name"
                  variant="outlined"
                  error={!!itemError}
                  helperText={itemError}
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        {getIngredientsLoading ? (
                          <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                        ) : null}
                        {params.InputProps.startAdornment}
                      </>
                    ),
                  }}
                />
              )}
              renderOption={(props, option) => (
                <Box
                  component="li"
                  {...props}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "8px 16px",
                    transition: "all 0.2s ease",
                    '&:hover': {
                      backgroundColor: "#f0f7ff",
                    }
                  }}
                >
                  <img
                    src={option.image_url || "/placeholder-16x9.png"}
                    alt={option.ingredient_name}
                    style={{
                      width: "40px",
                      height: "40px",
                      borderRadius: "50%",
                      objectFit: "cover",
                      border: "1px solid #e0e0e0"
                    }}
                    loading="lazy"
                  />
                  <Typography variant="body2">{option.ingredient_name}</Typography>
                </Box>
              )}
              noOptionsText={
                getIngredientsLoading ? (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", p: 1 }}>
                    <CircularProgress size={20} sx={{ mr: 1 }} />
                    <Typography>Loading ingredients...</Typography>
                  </Box>
                ) : (
                  <Box sx={{ p: 1 }}>
                    <Typography sx={{ mb: 1 }}>Ingredient not found in the list</Typography>
                    <Button 
                      variant="contained" 
                      startIcon={<AddCircleOutlineIcon />}
                      onClick={() => {
                        setItem_name(bowlName);
                        setAddIngredientActive(true);
                      }}
                      size="small"
                      sx={{ textTransform: "none" }}
                    >
                      Add New Ingredient
                    </Button>
                  </Box>
                )
              }
              onInputChange={(event, newInputValue) => setBowlName(newInputValue)}
              value={
                ingredients.find((item) => item.ingredient_name === bowlName) ||
                null
              }
              fullWidth
              disabled={getIngredientsLoading}
              loading={getIngredientsLoading}
              loadingText="Loading ingredients..."
            />
            
            <FormControl fullWidth variant="outlined">
              <TextField
                label="Quantity"
                type="number"
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                error={!!error}
                helperText={error || `Enter a value between ${minQuantity} and ${maxQuantity}`}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">grams</InputAdornment>
                  ),
                }}
                inputProps={{ min: minQuantity, max: maxQuantity }}
              />
            </FormControl>
            
            <Divider sx={{ my: 1 }} />
            
            <Button 
              type="submit" 
              variant="contained" 
              color="primary"
              disabled={isSubmitting || getIngredientsLoading}
              endIcon={isSubmitting ? <CircularProgress size={20} color="inherit" /> : <ArrowForwardIcon />}
              sx={{ 
                py: 1.5, 
                mt: 1,
                backgroundColor: "#1976d2",
                '&:hover': {
                  backgroundColor: "#1565c0"
                },
                transition: "all 0.3s ease"
              }}
            >
              {isSubmitting ? "Submitting..." : "Configure Bowl"}
            </Button>
          </Stack>
        </form>
      </Paper>
    </Slide>
  );
}
